/**
 * useAuthSync Hook - Sincronización periódica de permisos con el servidor
 *
 * QUÉ HACE:
 * - Hace polling periódico al endpoint /auth/me para mantener permisos sincronizados
 * - Detecta cambios en permisos/rol y actualiza el store local Y localStorage de sesión
 *
 * QUÉ EXPORTA: useAuthSync (named)
 *
 * QUÉ NO DEBE HACER:
 * - NO debe renderizar UI
 * - NO debe manejar logout (eso lo hace el interceptor de axios)
 * - NO debe llamar window.location.reload()
 *
 * REGLAS:
 * - SOLO debe usarse en MainLayout
 * - El intervalo de polling es configurable pero por defecto 30s
 * - El interceptor de axios maneja los errores 401/403 automáticamente
 *
 * Uso:
 *   useAuthSync(); // En MainLayout solamente
 */

import { useEffect, useRef } from "react";
import { useAuthStore } from "@/store/authStore";
import { authService } from "@/services/authService";
import type { Business, User } from "@/types";

const SYNC_INTERVAL_MS = 30_000; // 30 segundos

type SyncedUser = User & { business?: Business | null };

function getSyncedUser(response: unknown): SyncedUser | null {
  if (typeof response !== "object" || response === null) return null;

  const payload = response as { user?: unknown; id?: unknown };
  const candidate = payload.user ?? response;
  if (
    typeof candidate !== "object" ||
    candidate === null ||
    typeof (candidate as { id?: unknown }).id !== "string"
  ) {
    return null;
  }

  return candidate as SyncedUser;
}

export const useAuthSync = () => {
  const { user, updateUser, updateBusiness } = useAuthStore();
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!user?.id) return;

    // Evitar doble sync por StrictMode en dev
    if (hasSynced.current) return;
    hasSynced.current = true;

    let cancelled = false;

    const syncPermissions = async () => {
      try {
        const currentUser = useAuthStore.getState().user;
        if (!currentUser?.id || cancelled) return;

        const response = await authService.getMe();
        if (cancelled) return;

        const serverUser = getSyncedUser(response);
        if (serverUser) {
          const permissionsChanged =
            JSON.stringify(serverUser.permissions) !==
            JSON.stringify(currentUser.permissions);
          const roleChanged = serverUser.role !== currentUser.role;

          if (permissionsChanged || roleChanged) {
            updateUser({
              permissions: serverUser.permissions,
              role: serverUser.role,
            });
          }

          if (serverUser.business !== undefined) {
            updateBusiness(serverUser.business);
          }
        }
      } catch {
        // El interceptor de api.ts maneja 401/403 automáticamente
      }
    };

    /**
     * Actualización instantánea de permisos cuando el admin edita un empleado localmente.
     * Dispatcheado por localPersonalOps.updateLocalEmployee con { userId, permissions, role }.
     * Solo actualiza si el empleado editado es el usuario de la sesión actual.
     */
    const handleInstantSync = (
      e: CustomEvent<{ userId?: string; permissions?: unknown; role?: string }>,
    ) => {
      const { userId, permissions, role } = e.detail ?? {};
      const currentUser = useAuthStore.getState().user;
      // Ignorar si el evento no es para el usuario actual
      if (!userId || currentUser?.id !== userId) return;
      if (permissions !== undefined || role !== undefined) {
        updateUser({
          ...(permissions !== undefined && {
            permissions: permissions as typeof currentUser.permissions,
          }),
          ...(role !== undefined && {
            role: role as import("@/types").User["role"],
          }),
        });
      }
    };

    /**
     * Sincronización entre pestañas (cross-tab).
     * La clave `personal_empleados` se actualiza cuando el admin edita un empleado
     * desde OTRA pestaña. El evento `storage` solo se dispara en pestañas distintas
     * a la que hizo el cambio (comportamiento nativo del navegador).
     */
    const handleStorageSync = (e: StorageEvent) => {
      if (e.key !== "personal_empleados" || !e.newValue) return;
      try {
        const currentUser = useAuthStore.getState().user;
        if (!currentUser?.id) return;
        const employees: Array<{
          id: string;
          permissions?: unknown;
          rol?: string;
        }> = JSON.parse(e.newValue);
        const myData = employees.find((emp) => emp.id === currentUser.id);
        if (!myData) return;
        const updates: Partial<{ permissions: unknown; role: string }> = {};
        if (myData.permissions !== undefined)
          updates.permissions =
            myData.permissions as typeof currentUser.permissions;
        if (myData.rol !== undefined) updates.role = myData.rol;
        if (Object.keys(updates).length > 0) {
          updateUser(updates as Parameters<typeof updateUser>[0]);
        }
      } catch {
        /* JSON inválido — ignorar */
      }
    };

    window.addEventListener(
      "altenot:permissions-sync",
      handleInstantSync as EventListener,
    );
    window.addEventListener("storage", handleStorageSync);

    // NO llamar syncPermissions inmediatamente — dejar que la página cargue primero.
    const interval = setInterval(syncPermissions, SYNC_INTERVAL_MS);

    return () => {
      cancelled = true;
      hasSynced.current = false;
      clearInterval(interval);
      window.removeEventListener(
        "altenot:permissions-sync",
        handleInstantSync as EventListener,
      );
      window.removeEventListener("storage", handleStorageSync);
    };
  }, [user?.id, updateBusiness, updateUser]);
};
